import { cloneDeep, maxBy } from "lodash";

import {
  RedirectDto,
  RedirectFilter,
  ShopifyTemplateTypeType,
  UrlRedirectType,
} from "@intelligems/ig-types";
import * as rtk from "@reduxjs/toolkit";
import { type PayloadAction, createSelector } from "@reduxjs/toolkit";

import { initialRedirectState } from "../../api/initials";
import {
  ensureAllTemplateType,
  ensureRedirectGroupOrder,
  ensureTemplateSuffix,
  ensureTestGroupRedirects,
} from "../../api/reducers/redirects/redirectReducerActions";
import { getSpecificRedirectsUtil } from "../../features/experienceTests/components/redirects/getSpecificRedirects";
import { VariationFe } from "../../types/schemas/variations";
import { IRedirectsSliceState } from "../types/redirects";
import { IAppState } from "../types/store";

const sliceInitialRedirectsState: IRedirectsSliceState = {
  isSuccess: false,
  redirects: [] as RedirectDto[],
  storedRedirects: [] as RedirectDto[],
  redirectErrors: [] as string[],
};

const redirectsSlice = rtk.createSlice({
  name: "redirects",
  initialState: sliceInitialRedirectsState,
  reducers: {
    resetRedirects: (draft) => {
      draft.isSuccess = false;
      draft.redirects = [];
      draft.storedRedirects = [];
      draft.redirectErrors = [];
    },
    initRedirectsState: (
      draft,
      action: PayloadAction<{ redirects: RedirectDto[]; domain: string }>
    ) => {
      draft.isSuccess = true;
      draft.redirects = action.payload.redirects;
      draft.storedRedirects = action.payload.redirects;
      draft.redirectErrors = [];
    },
    initUrlRedirectsState: (
      draft,
      action: PayloadAction<{
        variations: VariationFe[];
        isPersonalization?: boolean;
      }>
    ) => {
      draft.isSuccess = true;
      draft.redirectErrors = [];

      action.payload.variations.forEach((testGroup: VariationFe) => {
        draft.redirects.push(
          initialRedirectState(
            testGroup,
            1,
            false,
            undefined,
            false,
            undefined,
            undefined,
            action.payload.isPersonalization
              ? !action.payload.isPersonalization
              : undefined
          )
        );
      });
      return draft;
    },
    checkRedirects: (
      draft,
      action: PayloadAction<{ variations: VariationFe[] }>
    ) => {
      draft.redirects = ensureTestGroupRedirects(
        draft.redirects,
        action.payload.variations
      );
      return draft;
    },
    addNewRedirectOrigin: (
      draft,
      action: PayloadAction<{
        variations: VariationFe[];
        filter: RedirectFilter;
      }>
    ) => {
      const group = (maxBy(draft.redirects, (_) => _.group)?.group || 0) + 1;
      action.payload.variations.forEach((testGroup: VariationFe) => {
        draft.redirects.push(
          initialRedirectState(
            testGroup,
            group,
            false,
            undefined,
            false,
            undefined,
            action.payload.filter
          )
        );
      });
      return draft;
    },
    updateAllRedirectFilters: (
      draft,
      action: PayloadAction<{
        filter: RedirectFilter;
      }>
    ) => {
      draft.redirects.forEach((redirect) => {
        redirect.filter = action.payload.filter;
      });
      return draft;
    },
    updateAllUrlRedirectType: (
      draft,
      action: PayloadAction<{
        redirectType: UrlRedirectType;
      }>
    ) => {
      draft.redirects.forEach((redirect) => {
        redirect.redirectType = action.payload.redirectType;
      });
      return draft;
    },
    deleteAllUrlRedirects: (draft) => {
      draft.redirects = draft.redirects.filter(
        (item) => item.isThemeTest || item.isTemplateTest
      );
      return draft;
    },
    updateOriginUrl: (
      draft,
      action: PayloadAction<{
        group: number;
        value: string;
        queryParams?: { key: string; value: string }[];
      }>
    ) => {
      for (const redirect of draft.redirects) {
        if (redirect.group === action.payload.group) {
          redirect.originUrl = action.payload.value;
          if (action.payload.queryParams) {
            action.payload.queryParams.forEach((queryParam) => {
              redirect.queryParams.push({
                key: queryParam.key,
                value: queryParam.value,
              });
            });
          }
        }
      }
      return draft;
    },
    updateDestinationUrl: (
      draft,
      action: PayloadAction<{
        redirectId: string;
        value: string;
        queryParams?: { key: string; value: string }[];
      }>
    ) => {
      const index = draft.redirects.findIndex(
        (redirect) => redirect.id === action.payload.redirectId
      );
      if (index !== -1) {
        draft.redirects[index].destinationUrl = action.payload.value;
        if (action.payload.queryParams) {
          action.payload.queryParams.forEach((queryParam) => {
            draft.redirects[index].queryParams.push({
              key: queryParam.key,
              value: queryParam.value,
            });
          });
        }
      }
      return draft;
    },
    deleteRedirectOrigin: (
      draft,
      action: PayloadAction<{
        group: number;
      }>
    ) => {
      draft.redirects = ensureRedirectGroupOrder(
        draft.redirects.filter((item) => item.group !== action.payload.group)
      );
      return draft;
    },
    updateRedirectGroupFilter: (
      draft,
      action: PayloadAction<{ group: number; filter: RedirectFilter }>
    ) => {
      for (const redirect of draft.redirects) {
        if (redirect.group === action.payload.group) {
          redirect.filter = action.payload.filter;
        }
      }
      return draft;
    },
    updateRedirectType: (
      draft,
      action: PayloadAction<{ group: number; redirectType: UrlRedirectType }>
    ) => {
      for (const redirect of draft.redirects) {
        if (redirect.group === action.payload.group) {
          redirect.redirectType = action.payload.redirectType;
          if (action.payload.redirectType === "fullReplacement") {
            redirect.find = undefined;
          }
        }
      }
      return draft;
    },
    updateRedirectFind: (
      draft,
      action: PayloadAction<{ redirectId: string; find: string }>
    ) => {
      const index = draft.redirects.findIndex(
        (redirect) => redirect.id === action.payload.redirectId
      );
      if (index !== -1) {
        draft.redirects[index].find = action.payload.find;
      }
      return draft;
    },
    updateQueryParam: (
      draft,
      action: PayloadAction<{
        redirectId: string;
        queryParamIndex: number;
        key: string;
        value: string;
      }>
    ) => {
      const index = draft.redirects.findIndex(
        (redirect) => redirect.id === action.payload.redirectId
      );
      if (index !== -1) {
        draft.redirects[index].queryParams[action.payload.queryParamIndex] = {
          key: action.payload.key,
          value: action.payload.value,
        };
      }
      return draft;
    },
    deleteQueryParam: (
      draft,
      action: PayloadAction<{
        redirectId: string;
        queryParamIndex: number;
        isControl?: boolean;
      }>
    ) => {
      const index = draft.redirects.findIndex(
        (redirect) => redirect.id === action.payload.redirectId
      );
      if (index !== -1) {
        draft.redirects[index].queryParams.splice(
          action.payload.queryParamIndex,
          1
        );
        // for template redirects
        if (action.payload.isControl) {
          draft.redirects[index].templateType = null;
        }
      }
      return draft;
    },
    addQueryParam: (
      draft,
      action: PayloadAction<{
        redirectId: string;
      }>
    ) => {
      const index = draft.redirects.findIndex(
        (redirect) => redirect.id === action.payload.redirectId
      );
      if (index !== -1) {
        draft.redirects[index].queryParams.push({
          key: "",
          value: "",
        });
      }
      return draft;
    },
    updateRedirectOnce: (
      draft,
      action: PayloadAction<{
        group: number;
        value: boolean;
      }>
    ) => {
      draft.redirects.forEach((redirect) => {
        if (redirect.group === action.payload.group) {
          redirect.redirectOnce = action.payload.value;
        }
      });
      return draft;
    },
    initializeThemeRedirect: (
      draft,
      action: PayloadAction<{
        variations: VariationFe[];
        isPersonalization?: boolean;
      }>
    ) => {
      const group = (maxBy(draft.redirects, (_) => _.group)?.group || 0) + 1;
      action.payload.variations.forEach((variation: VariationFe) => {
        draft.redirects.push(
          initialRedirectState(
            variation,
            group,
            true,
            undefined,
            undefined,
            undefined,
            undefined,
            action.payload.isPersonalization
              ? !action.payload.isPersonalization
              : undefined
          )
        );
      });
      return draft;
    },
    updateTemplateRedirect: (
      draft,
      action: PayloadAction<{
        redirectState: RedirectDto;
        isControl: boolean;
      }>
    ) => {
      let draftCopy: RedirectDto[] = cloneDeep(draft.redirects);

      const index = draftCopy.findIndex(
        (red) =>
          red.variationId === action.payload.redirectState.variationId &&
          red.isTemplateTest
      );

      if (index !== -1) {
        draftCopy[index] = action.payload.redirectState;

        if (action.payload.isControl) {
          draftCopy = ensureAllTemplateType(
            draftCopy,
            action.payload.redirectState.templateType!
          );
        }

        draftCopy = ensureTemplateSuffix(draftCopy);
      }
      draft.redirects = draftCopy;
      return draft;
    },
    updatePersonalizationTemplateRedirect: (
      draft,
      action: PayloadAction<{
        redirectState: RedirectDto;
        index: number;
      }>
    ) => {
      const draftCopy: RedirectDto[] = cloneDeep(draft.redirects);

      draftCopy[0].templateType = action.payload.redirectState.templateType;
      if (action.payload.index === 0) {
        // control and only update suffix
        draftCopy[0].templateSuffixes =
          action.payload.redirectState.templateSuffixes;
      } else {
        draftCopy[0].queryParams = action.payload.redirectState.queryParams;
        // update query param
      }

      // draftCopy = ensureTemplateSuffix(draftCopy);

      draft.redirects = draftCopy;
      return draft;
    },
    updateThemeRedirect: (
      draft,
      action: PayloadAction<{
        redirectState: RedirectDto;
      }>
    ) => {
      const draftCopy: RedirectDto[] = cloneDeep(draft.redirects);

      const index = draftCopy.findIndex(
        (red) =>
          red.variationId === action.payload.redirectState.variationId &&
          red.isThemeTest
      );

      if (index !== -1) {
        draftCopy[index] = action.payload.redirectState;
      }
      draft.redirects = draftCopy;
      return draft;
    },
    deleteAllTemplateOrigins: (draft) => {
      draft.redirects = draft.redirects.filter((item) => !item.isTemplateTest);
      return draft;
    },
    deleteThemeRedirects: (draft) => {
      draft.redirects = draft.redirects.filter((item) => !item.isThemeTest);
      return draft;
    },
    initializeTemplateRedirects: (
      draft,
      action: PayloadAction<{
        variations: VariationFe[];
        templateType: ShopifyTemplateTypeType | null;
        isPersonalization?: boolean;
      }>
    ) => {
      action.payload.variations.forEach((testGroup: VariationFe) => {
        draft.redirects.push(
          initialRedirectState(
            testGroup,
            1,
            false,
            undefined,
            true,
            undefined,
            undefined,
            action.payload.isPersonalization
              ? !action.payload.isPersonalization
              : undefined
          )
        );
      });
      return draft;
    },
    setRedirectErrors: (
      draft,
      action: PayloadAction<{
        error: string | null;
        redirectId: string;
        urlKey: string;
      }>
    ) => {
      if (action.payload.error) {
        if (!draft.redirectErrors.includes(action.payload.error)) {
          draft.redirectErrors.push(action.payload.error);
        }
      } else {
        const index = draft.redirectErrors.findIndex(
          (err) =>
            err === `${action.payload.urlKey}-${action.payload.redirectId}`
        );
        if (index !== -1) {
          draft.redirectErrors.splice(index, 1);
        }
      }
      return draft;
    },
    removeDeletedRedirectErrors: (draft) => {
      const currentRedirectErrors: string[] = [];
      const allRedirectIds = draft.redirects.map((redirect) => redirect.id);

      draft.redirectErrors.forEach((error) => {
        const formattedError = error.replace(
          /^(originUrl-|destinationUrl-|queryParameter-)/,
          ""
        );

        if (allRedirectIds.includes(formattedError)) {
          currentRedirectErrors.push(error);
        }
      });
      draft.redirectErrors = currentRedirectErrors;
      return draft;
    },
    resetRedirectErrors: (draft) => {
      draft.redirectErrors = [];
      return draft;
    },
  },
  selectors: {
    selectedRedirectsState: (state) => state as IRedirectsSliceState,
    selectedRedirects: (state) => state.redirects as RedirectDto[],
    selectedStoredRedirects: (state) => state.storedRedirects as RedirectDto[],
    selectedRedirectErrors: (state) => state.redirectErrors as string[],
  },
});

export const {
  resetRedirects,
  initRedirectsState,
  initUrlRedirectsState,
  checkRedirects,
  addNewRedirectOrigin,
  updateAllRedirectFilters,
  deleteAllUrlRedirects,
  updateOriginUrl,
  updateDestinationUrl,
  deleteRedirectOrigin,
  updateRedirectGroupFilter,
  updateRedirectType,
  updateRedirectFind,
  updateQueryParam,
  deleteQueryParam,
  addQueryParam,
  updateRedirectOnce,
  initializeThemeRedirect,
  deleteAllTemplateOrigins,
  deleteThemeRedirects,
  updateTemplateRedirect,
  updateThemeRedirect,
  initializeTemplateRedirects,
  setRedirectErrors,
  removeDeletedRedirectErrors,
  resetRedirectErrors,
  updateAllUrlRedirectType,
  // personalization
  updatePersonalizationTemplateRedirect,
} = redirectsSlice.actions;

export const redirectsReducer = redirectsSlice.reducer;
export const {
  selectedRedirectsState,
  selectedRedirects,
  selectedStoredRedirects,
  selectedRedirectErrors,
} = redirectsSlice.selectors;

export const getSliceSpecificRedirects = createSelector(
  // Input selectors
  (state: IAppState) => state,
  (_: IAppState, redirectType: "template" | "theme" | "url") => redirectType,
  // Output selector
  (state, redirectType) => {
    return getSpecificRedirectsUtil(state.redirects.redirects, redirectType);
  }
);

// export const getSliceSpecificRedirects = createSelector<
//   [RedirectsState, string],
//   ReturnType<typeof getSpecificRedirectsUtil>
// >(
//   // Input selectors
//   (state: RedirectsState) => state.redirects,
//   (_, redirectType) => redirectType,
//   // Output selector
//   (state, redirectType) => {
//     return getSpecificRedirectsUtil(state.redirects, redirectType);
//   }
// );
